import makeStyles from '@mui/styles/makeStyles';
import {isNumberKey} from "common/helpers";
import {color, font, type} from "common/tokens";
import { map, padStart} from 'lodash';
import React from "react";
import i18n from "i18n";

let classNames = require('classnames');


const CIRCLE_SIZE = 44;

const useCircleStyles = makeStyles({
  scale: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  option: {
    flex: '1 1 0',
    // The circle's own width, so a ten-point scale still fits one row inside
    // the quiz dialog. The columns share the space evenly above that floor.
    minWidth: CIRCLE_SIZE,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  circle: {
    ...type.body,
    fontWeight: font.SEMIBOLD,
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    boxSizing: 'border-box',
    borderRadius: '50%',
    border: `1px solid ${color.BORDER}`,
    background: color.SURFACE_MUTED,
    color: color.TEXT_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      background: color.ACCENT_TINT,
      color: color.ACCENT_TEXT,
    },
  },
  selected: {
    background: color.ACCENT_TINT,
    border: `2px solid ${color.ACCENT}`,
    color: color.ACCENT_TEXT,
  },
  label: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    textAlign: 'center',
    margin: 0,
  },
  endLabel: {
    ...type.chipSmall,
    color: color.TEXT_MUTED,
  },
  input: {
    position: 'absolute',
    opacity: 0,
    width: 0,
    height: 0,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  rowLabel: {
    ...type.body,
    color: color.TEXT,
    margin: 0,
  },
});

export const ScaleCircles = ({options, value, questionKey, onSelect, onKeyDown, endLabels = false, stacked = false}) => {
  const classes = useCircleStyles();
  const lastIndex = options.length - 1;

  const circle = option => (
    <>
      <input
        id={`${option.value}-${questionKey}`}
        className={classes.input}
        type={'radio'}
        name={questionKey}
        value={option.value}
        checked={option.value === value}
        onChange={() => onSelect(option.value)}
      />
      <label
        htmlFor={`${option.value}-${questionKey}`}
        className={classNames(classes.circle, option.value === value && classes.selected)}
      >
        {padStart(option.value, 2, '0')}
      </label>
    </>
  );

  if (stacked) {
    return (
      <div className={classes.column} onKeyDown={onKeyDown}>
        {map(options, option => (
          <div key={option.value} className={classes.row}>
            {circle(option)}
            <p className={classes.rowLabel}>{option.label}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={classes.scale} onKeyDown={onKeyDown}>
      {map(options, (option, index) => {
        const showLabel = !endLabels || index === 0 || index === lastIndex;
        return (
          <div key={option.value} className={classes.option}>
            {circle(option)}
            {showLabel && Boolean(option.label) && (
              <p className={classNames(classes.label, endLabels && classes.endLabel)}>{option.label}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};


const AgreeabilityScale = ({onAnswered, value, maxLabel = i18n.t('completelyAgree'), minLabel = i18n.t('completelyDisagree'), question: {key}, ...rest}) => {

  const {
    minimumValue = 1,
    maximumValue = 10
  } = rest;

  const handleChange = (n) => {
    onAnswered(n);
  };

  const handleKeyPress = (event) => {
    if (isNumberKey(event)) {
      const keyNumber = parseInt(event.key);
      if (keyNumber <= maximumValue && keyNumber >= minimumValue) {
        onAnswered(keyNumber);
      }
    }
  };


  const options = [];
  for (let i = minimumValue; i <= maximumValue; i++) {
    options.push(i);
  }
  return (
    <ScaleCircles
      endLabels
      options={options.map(option => ({
        value: option,
        label: option === minimumValue ? minLabel : option === maximumValue ? maxLabel : '',
      }))}
      value={value}
      questionKey={key}
      onSelect={handleChange}
      onKeyDown={handleKeyPress}
    />
  );
};


export default (AgreeabilityScale);
